// packages/frontend/src/services/apiMedia.tsx
// Media Library API helpers (session-cookie first; x-user-id optional fallback)

const MEDIA_API_BASE =
  (import.meta as any)?.env?.VITE_MEDIA_BACKEND_URL || '/media/api';
const DEFAULT_TIMEOUT_MS = 15_000;

/** Safely encode a path like "<cid>/path/to/file.enc" without double-encoding slashes */
function encodePathSegments(p: string): string {
  return String(p)
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

async function toJSON(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    const text = await res.text().catch(() => '');
    return text ? { message: text } : null;
  }
}

type HttpOpts = {
  method?: string;
  userId?: string; // optional legacy header (server can ignore if using cookies)
  headers?: Record<string, string>;
  body?: any;
  raw?: boolean; // for blob/arrayBuffer responses
  timeoutMs?: number;
  credentials?: RequestCredentials;
};

/** Core fetch with JSON handling, cookies, and timeout */
async function http(path: string, opts: HttpOpts = {}): Promise<any> {
  const {
    method = 'GET',
    userId,
    headers = {},
    body,
    raw = false,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    credentials = 'include',
  } = opts;

  const controller = new AbortController();
  const to = setTimeout(() => controller.abort('timeout'), timeoutMs);

  // Only set JSON header when sending a (non-raw) JSON body
  const h: Record<string, string> = {
    ...(body != null && !raw ? { 'Content-Type': 'application/json' } : {}),
    ...(userId ? { 'x-user-id': userId } : {}),
    ...headers,
  };

  let res: Response;
  try {
    res = await fetch(`${MEDIA_API_BASE}${path}`, {
      method,
      headers: h,
      body: body != null && !raw ? JSON.stringify(body) : body,
      credentials,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(to);
  }

  if (raw) {
    if (!res.ok) throw new Error(`${method} ${path} failed (${res.status})`);
    return res; // caller decides .blob() / .arrayBuffer()
  }

  const data = await toJSON(res);
  if (!res.ok) {
    const err: any = new Error(
      data?.error || data?.message || `${method} ${path} failed (${res.status})`
    );
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/* ============================== Session / Me ============================== */

/** GET /api/me -> { authenticated, env, user, ownedProducts[], memberships[], hasAccess } */
export async function getMe(userId?: string) {
  return http('/me', { userId });
}

/* ============================== Products ================================== */

/** GET /api/products -> owned-aware list when session present */
export async function getProducts(userId?: string) {
  return http('/products', { userId });
}

/** GET /api/product/:id -> product detail + public/private files */
export async function getProductDetail(productId: string, userId?: string) {
  return http(`/product/${encodeURIComponent(productId)}`, { userId });
}

/* ============================ Video (Livepeer) ============================ */

/** POST /api/video/token -> { token, expiresAt } (requires ownership) */
export async function getVideoToken(params: {
  productId: string;
  playbackId: string;
  ttlSeconds?: number;
  userId?: string;
}) {
  const { productId, playbackId, ttlSeconds = 120, userId } = params || {};
  return http('/video/token', {
    method: 'POST',
    userId,
    body: { productId, playbackId, ttlSeconds },
  });
}

/* ============================= Images (Lit) =============================== */

/**
 * GET /api/secure/image/:productId/:securePath
 * Returns: Blob (use URL.createObjectURL)
 */
export async function fetchSecureImageBlob(args: {
  productId: string;
  securePath: string;
  sessionSigs?: unknown;
  userId?: string;
}): Promise<Blob> {
  const { productId, securePath, sessionSigs, userId } = args;
  const encodedPath = encodePathSegments(securePath);
  const res = (await http(
    `/secure/image/${encodeURIComponent(productId)}/${encodedPath}`,
    {
      method: 'GET',
      userId,
      headers: {
        'x-lit-session-sigs': JSON.stringify(sessionSigs || {}),
      },
      raw: true,
    }
  )) as Response;
  return res.blob();
}

/* ================================ Favorites =============================== */

/** GET /api/favorites -> { productFavorites: string[], fileFavorites: { [fileId]: { full, timestamps[] } } } */
export async function getFavorites(userId?: string) {
  return http('/favorites', { userId });
}

/** Product-level favorite toggle */
export async function saveProductFavorite(
  productId: string,
  isFav: boolean,
  userId?: string
) {
  return http('/favorites', {
    method: 'POST',
    userId,
    body: { productId, isFav },
  });
}

/** File-level "full" favorite state */
export async function saveFileFavoriteState(
  fileId: string,
  full: boolean,
  userId?: string
) {
  return http('/favorites', {
    method: 'POST',
    userId,
    body: { fileId, full: !!full },
  });
}

/** File-level timestamps (replace set) */
export async function saveFileFavoriteTimestamps(
  fileId: string,
  timestamps: number[],
  userId?: string
) {
  return http('/favorites', {
    method: 'POST',
    userId,
    body: { fileId, timestamps: Array.isArray(timestamps) ? timestamps : [] },
  });
}

/* ============================== Legacy (optional) ========================= */
/**
 * Legacy write-through for old structure { [mediaId]: [timestamps | "__FULL__"] }
 * Prefer the new helpers above. Provided for backward compatibility.
 */
export async function saveFavoritesLegacy(
  userId: string,
  mediaId: string,
  timestamps: (number | '__FULL__')[]
) {
  return http('/favorites', {
    method: 'POST',
    userId,
    body: { identifier: userId, mediaId, timestamps },
  });
}
